import { useEffect, useRef, useState } from 'react'
import { Download, Eye, X } from 'lucide-react'
import { extractTicket, prettyDate } from './pdfParser'
import { downloadReservationPdf } from './pdfExport'
import { nextReservationIds } from './idSequence'
import { formatClientName, initializeHotelTemplate, readHotelTemplateValues } from './hotelBookingTemplate'
import type { ParsedTicket, ReservationValues } from './types'

const initialValues: ReservationValues = {
  bookingId: '', bookingReference: '', memberId: '', client: '', rooms: '1', adults: '1', roomType: 'Superior Twin Room', guestList: '',
}

function App() {
  const inputRef = useRef<HTMLInputElement>(null)
  const templateFrameRef = useRef<HTMLIFrameElement>(null)
  const previewHeaderRef = useRef<HTMLDivElement>(null)
  const [ticket, setTicket] = useState<ParsedTicket | null>(null)
  const [fileName, setFileName] = useState('')
  const [values, setValues] = useState(initialValues)
  const [error, setError] = useState('')
  const [generated, setGenerated] = useState(false)
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [templateReady, setTemplateReady] = useState(false)
  const [templateVersion, setTemplateVersion] = useState(0)
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    if (!previewOpen) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPreviewOpen(false)
    }
    const previousOverflow = document.body.style.overflow
    document.addEventListener('keydown', closeOnEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', closeOnEscape)
      document.body.style.overflow = previousOverflow
    }
  }, [previewOpen])

  useEffect(() => {
    if (generated && templateReady) {
      const isMobile = window.matchMedia('(max-width: 560px)').matches
      previewHeaderRef.current?.scrollIntoView({ behavior: 'smooth', block: isMobile ? 'center' : 'start' })
    }
  }, [generated, templateReady])
  async function parseFile(file?: File) {
    if (!file) return
    setBusy(true); setError(''); setFileName(file.name)
    try {
      const parsed = await extractTicket(file)
      setTicket(parsed)
      setGenerated(false)
      setTemplateReady(false)
    } catch (reason) {
      setTicket(null)
      setError(reason instanceof Error ? reason.message : 'Could not read this PDF.')
    } finally { setBusy(false) }
  }

  async function loadSample(path = '/sample-flight-ticket.pdf', name = 'sample-flight-ticket.pdf') {
    setBusy(true)
    setError('')
    try {
      const response = await fetch(path)
      if (!response.ok) throw new Error(`Could not load the sample ticket (${response.status}).`)
      const blob = await response.blob()
      await parseFile(new File([blob], name, { type: 'application/pdf' }))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not load the sample ticket.')
    } finally {
      setBusy(false)
    }
  }

  function generateReservation() {
    if (!ticket) return
    const ids = nextReservationIds()
    setValues({ ...initialValues, ...ids, client: formatClientName(ticket.guestName), guestList: ticket.guestName })
    setTemplateReady(false)
    setTemplateVersion((version) => version + 1)
    setGenerated(true)
  }

  function updateValue(key: keyof ReservationValues, value: string) {
    setValues((current) => ({ ...current, [key]: value }))
  }

  async function downloadReservation() {
    const doc = templateFrameRef.current?.contentDocument
    const page = doc?.getElementById('page')
    if (!ticket || !doc || !page || !templateReady || exporting) return
    setExporting(true)
    setError('')
    try {
      const currentValues = readHotelTemplateValues(doc, values)
      const clientField = doc.getElementById('client')
      if (clientField) clientField.textContent = currentValues.client
      setValues(currentValues)
      await downloadReservationPdf(page, currentValues.bookingId)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not create the reservation PDF.')
    } finally {
      setExporting(false)
    }
  }

  function handleTemplateLoad() {
    const doc = templateFrameRef.current?.contentDocument
    if (!doc || !ticket) return
    setTemplateReady(initializeHotelTemplate(doc, ticket, values, updateValue))
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand-mark"><span className="brand-icon">✦</span><span>FlightStay</span></div>
        <div className="topbar-note">Local PDF extraction · No paid APIs</div>
      </header>



      <section className="workspace">
        <aside className="control-rail">
          <div className="step-card">
            <div className="step-heading"><span className="step-number">1</span><div><p className="section-kicker">SOURCE DOCUMENT</p><h2>Upload ticket</h2></div></div>
            <input ref={inputRef} className="file-input" type="file" accept="application/pdf,.pdf" onChange={(event) => parseFile(event.target.files?.[0])} />
            <button className="primary-button" onClick={() => inputRef.current?.click()} disabled={busy}>{busy ? 'Reading PDF…' : 'Choose PDF ticket'}</button>
            <button className="text-button" onClick={() => loadSample()} disabled={busy}>Load Travelport sample ticket</button>
            <button className="text-button" onClick={() => loadSample('/sample-emirates-ticket.pdf', 'sample-emirates-ticket.pdf')} disabled={busy}>Load Emirates sample ticket</button>
            <button className="text-button" onClick={() => loadSample('/sample-airarabia-ticket.pdf', 'sample-airarabia-ticket.pdf')} disabled={busy}>Load Air Arabia sample ticket</button>
            {fileName && <p className="file-name">{fileName}</p>}
            <p className="privacy-note">Uploaded tickets stay on your device; extraction and PDF creation run locally. Built-in demo tickets contain synthetic data.</p>
          </div>

          <div className="step-card extraction-card">
            <div className="step-heading"><span className="step-number">2</span><div><p className="section-kicker">VERIFY DETAILS</p><h2>Ticket data</h2></div></div>
            {ticket ? <div className="data-list"><DataRow label="Ticket owner" value={ticket.guestName} /><DataRow label="Hotel arrival" value={prettyDate(ticket.arrivalDate)} /><DataRow label="Hotel departure" value={prettyDate(ticket.departureDate)} /></div> : <p className="empty-state">Your extracted name and dates will appear here.</p>}
            {error && <div className="error-box">{error}</div>}
            <button className="secondary-button" onClick={generateReservation} disabled={!ticket || busy}>Generate reservation <span>↗</span></button>
          </div>

        </aside>

        <section className="preview-panel">
          <div className="preview-header" ref={previewHeaderRef}>
            <div><p className="section-kicker">LIVE PREVIEW</p><h2>{generated ? 'Hotel booking confirmation' : 'Your reservation will appear here'}</h2></div>
            {generated && templateReady && <>
              <button className="print-button desktop-download" onClick={downloadReservation} disabled={exporting || !templateReady}>{exporting ? 'Preparing PDF…' : 'Download reservation PDF'} <span>↓</span></button>
              <div className="preview-actions">
                <button className="preview-icon-button" onClick={downloadReservation} disabled={exporting || !templateReady} aria-label="Download reservation PDF" title="Download reservation PDF"><Download size={19} aria-hidden="true" /></button>
                <button className="preview-icon-button" onClick={() => setPreviewOpen(true)} disabled={!templateReady} aria-label="Preview reservation" title="Preview reservation"><Eye size={19} aria-hidden="true" /></button>
              </div>
            </>}
          </div>
          {generated && !templateReady && <div className="reservation-preparing" role="status">
            <span>Preparing your reservation preview…</span>
            <div className="reservation-progress" role="progressbar" aria-label="Preparing reservation preview" aria-valuemin={0} aria-valuemax={100}>
              <span />
            </div>
          </div>}          {!generated ? <div className="preview-empty"><div className="empty-symbol">&#10023;</div><h3>Waiting for a verified ticket</h3><p>Upload the supplied sample or another supported flight-ticket PDF to build a reservation.</p></div> : <>
            <p className="template-hint">Edit the eight fields directly in the booking form. Arrival and departure dates are filled from your ticket.</p>
            <div className={`template-modal-shell${previewOpen ? ' is-open' : ''}`} role={previewOpen ? "dialog" : undefined} aria-modal={previewOpen || undefined} aria-label="Hotel reservation preview">
              <div className="template-modal-header">
                <strong>Hotel reservation preview</strong>
                <div>
                  <button className="preview-icon-button modal-download" onClick={downloadReservation} disabled={exporting || !templateReady} aria-label="Download reservation PDF" title="Download reservation PDF"><Download size={19} aria-hidden="true" /></button>
                  <button className="preview-icon-button modal-close" onClick={() => setPreviewOpen(false)} aria-label="Close preview" title="Close preview"><X size={19} aria-hidden="true" /></button>
                </div>
              </div>
              <div className="template-frame-container">
                {!templateReady && <div className="template-loading">Preparing the editable booking template&#8230;</div>}
                <iframe key={templateVersion} ref={templateFrameRef} className="template-iframe" src="/hotel-booking-template.html" title="Editable hotel booking confirmation" onLoad={handleTemplateLoad} style={{ visibility: templateReady ? 'visible' : 'hidden' }} />
              </div>
            </div>
          </>}
        </section>
      </section>

      <footer className="footer"><span>FLIGHTSTAY / HOTEL RESERVATION BUILDER</span><span>Browser-only workflow · Your PDFs stay on your device</span></footer>
    </main>
  )
}

function DataRow({ label, value }: { label: string; value: string }) { return <div className="data-row"><span>{label}</span><strong>{value}</strong></div> }

export default App
